/**
 * Dronecast Protocol v1.0
 *
 * 메시지 타입 네이밍 규칙:
 *   cmd.{대상}.{동작}     — 클라이언트 → 서버 요청
 *   res.{대상}.{동작}     — 서버 → 클라이언트 응답 (cmd와 1:1 대응)
 *   evt.{대상}.{이벤트명}  — 서버 → 클라이언트 이벤트 (브로드캐스트)
 */

import type { VideoSourceType } from './video-source.types'
import type {
  RecordingFormat,
  RecordingTrigger,
  RecordingStatus,
  RecordingResult,
  ActiveRecording,
  RecordingPolicy,
} from './recording.types'

export const DRONECAST_PROTOCOL = 'dronecast/1.0'

// ── Command (Client → Server) ────────────────────────────

export type DCCommandType =
  | 'cmd.castroom.create'
  | 'cmd.castroom.join'
  | 'cmd.castroom.leave'
  | 'cmd.castroom.close'
  | 'cmd.casting.start'
  | 'cmd.stream.subscribe'
  | 'cmd.stream.unsubscribe'
  | 'cmd.ice.candidate'
  | 'cmd.rtsp_caster.create'
  | 'cmd.rtsp_caster.stop'
  | 'cmd.rtsp_caster.list'
  | 'cmd.recording.start'
  | 'cmd.recording.stop'
  | 'cmd.recording.list'
  | 'cmd.recording.set_policy'
  | 'cmd.recording.get_policy'

// ── Response (Server → Client) ───────────────────────────

export type DCResponseType =
  | 'res.castroom.create'
  | 'res.castroom.join'
  | 'res.castroom.leave'
  | 'res.castroom.close'
  | 'res.casting.start'
  | 'res.stream.subscribe'
  | 'res.stream.unsubscribe'
  | 'res.rtsp_caster.create'
  | 'res.rtsp_caster.stop'
  | 'res.rtsp_caster.list'
  | 'res.recording.start'
  | 'res.recording.stop'
  | 'res.recording.list'
  | 'res.recording.set_policy'
  | 'res.recording.get_policy'

// ── Event (Server → Client, 브로드캐스트) ─────────────────

export type DCEventType =
  | 'evt.castroom.status_changed'
  | 'evt.castroom.viewers_updated'
  | 'evt.viewer.status_changed'
  | 'evt.media.state_changed'
  | 'evt.media.quality_report'
  | 'evt.server.health'
  | 'evt.rtsp_caster.status'
  | 'evt.recording.status_changed'
  | 'evt.recording.completed'
  | 'evt.recording.triggered'

// ── 통합 메시지 타입 ──────────────────────────────────────

export type DCMessageType = DCCommandType | DCResponseType | DCEventType

// ── 기존 메시지 타입 (하위 호환) ──────────────────────────

export type DCLegacyMessageType =
  | 'create_castroom'
  | 'create_castroom_result'
  | 'join_castroom'
  | 'join_castroom_result'
  | 'cast_stream'
  | 'cast_stream_result'
  | 'subscribe_stream'
  | 'subscribe_stream_result'
  | 'subscribe_stream_answer'
  | 'exchange_icecandidate'
  | 'leave_castroom'
  | 'leave_castroom_result'
  | 'close_castroom'
  | 'close_castroom_result'
  | 'castroom_status_changed'
  | 'viewer_list_changed'
  | 'viewer_status_changed'
  | 'caster_link_status_changed'
  | 'on_communication_error'
  | 'on_communication_closed'

// ── 기존 → 신규 매핑 ─────────────────────────────────────

export const LEGACY_TO_NEW_CMD: Record<string, DCCommandType> = {
  'create_castroom': 'cmd.castroom.create',
  'join_castroom': 'cmd.castroom.join',
  'leave_castroom': 'cmd.castroom.leave',
  'close_castroom': 'cmd.castroom.close',
  'cast_stream': 'cmd.casting.start',
  'subscribe_stream': 'cmd.stream.subscribe',
  'exchange_icecandidate': 'cmd.ice.candidate',
}

export const LEGACY_TO_NEW_RES: Record<string, DCResponseType> = {
  'create_castroom_result': 'res.castroom.create',
  'join_castroom_result': 'res.castroom.join',
  'leave_castroom_result': 'res.castroom.leave',
  'close_castroom_result': 'res.castroom.close',
  'cast_stream_result': 'res.casting.start',
  'subscribe_stream_result': 'res.stream.subscribe',
}

export const LEGACY_TO_NEW_EVT: Record<string, DCEventType> = {
  'castroom_status_changed': 'evt.castroom.status_changed',
  'viewer_list_changed': 'evt.castroom.viewers_updated',
  'viewer_status_changed': 'evt.viewer.status_changed',
}

// ── 메시지 구조 ──────────────────────────────────────────

export interface IDCMessage {
  protocol: typeof DRONECAST_PROTOCOL
  type: DCMessageType | DCLegacyMessageType
  payload: DCPayload
}

// ── Castroom ID ──────────────────────────────────────────
// CastroomId와 동일 구조. 프로토콜 레이어에서 사용하는 별칭.

export interface DCCastroomId {
  gcs: string
  drone: string
  camera: string
}

// ── 미디어 서버 타입 ─────────────────────────────────────

/**
 * 미디어 서버 타입.
 *
 * **Browserless(headless relay) 단일이다** — Kurento 경로는 ADR-B25 로 제거됐다.
 * 값이 하나뿐이지만 필드를 남기는 이유는 `dronecast/1.0` 계약이 이 필드를 유지하기 때문이다
 * (`code/shared/protocol/01_messages.md` §2.1).
 */
export type MediaServerType = 'headless'

// ── Payload 타입 ─────────────────────────────────────────

export type DCResultStatus = 'success' | 'error' | 'retry'

/** 캐스트룸 종료 사유 (BL-611) */
export type DCCastroomCloseReason = 'caster_stopped' | 'admin_closed' | 'error'

export interface DCPayloadCastroomCreate {
  castroomId: DCCastroomId
  rtspUrl?: string
  mediaServerType?: MediaServerType
  /** 영상 소스 입력 유형 (DES_CMN_영상소스정보조회_입력유형별분석.md §9) */
  sourceType?: VideoSourceType
  /**
   * 캐스터가 미디어 서버와 다른 호스트에 있는지 여부.
   *
   * 지정하지 않으면 rtspUrl 유무로 추론한다(하위 호환). 그러나 그 추론은
   * 서버측 RTSP 캐스터를 전제한 것이라 온보드 캐스터에는 맞지 않는다 —
   * 진단 목적으로라도 rtspUrl 을 함께 보내면 local 로 오판정되어
   * host 후보만 나가고 원격 드론이 붙지 못한다.
   *
   * 온보드 캐스터는 항상 true 를 명시한다.
   */
  remoteCaster?: boolean
}

export interface DCPayloadCastroomCreateResult {
  castroomId: DCCastroomId
  result: DCResultStatus
  data?: DCCastroomInfo
  error?: string
}

export interface DCPayloadCastroomJoin {
  castroomId: DCCastroomId
  retryCount?: number
}

export interface DCPayloadCastroomJoinResult {
  castroomId: DCCastroomId
  result: DCResultStatus
  data?: DCCastroomInfo | number
  error?: string
}

export interface DCPayloadCastroomLeave {
  castroomId: DCCastroomId
}

export interface DCPayloadCastroomLeaveResult {
  castroomId: DCCastroomId
  result: DCResultStatus
  error?: string
}

export interface DCPayloadCastroomClose {
  castroomId: DCCastroomId
  /** 종료 사유 — admin이 종료한 경우 'admin_closed' 지정 (BL-611) */
  reason?: DCCastroomCloseReason
}

export interface DCPayloadCastroomCloseResult {
  castroomId: DCCastroomId
  result: DCResultStatus
  error?: string
}

export interface DCPayloadCastingStart {
  castroomId: DCCastroomId
  sdpOffer: string
}

export interface DCPayloadCastingStartResult {
  castroomId: DCCastroomId
  result: DCResultStatus
  data?: string // sdpAnswer
  error?: string
}

export interface DCPayloadStreamSubscribe {
  castroomId: DCCastroomId
  sdpOffer: string
}

export interface DCPayloadStreamSubscribeResult {
  castroomId: DCCastroomId
  result: DCResultStatus
  data?: string // sdpAnswer
  error?: string
}

export interface DCPayloadStreamUnsubscribe {
  castroomId: DCCastroomId
}

export interface DCPayloadStreamUnsubscribeResult {
  castroomId: DCCastroomId
  result: DCResultStatus
  error?: string
}

export interface DCPayloadIceCandidate {
  castroomId: DCCastroomId
  type: 'caster' | 'viewer'
  candidate: unknown
}

export interface DCPayloadCastroomStatusChanged {
  castroomId: DCCastroomId
  previousStatus: string | undefined
  currentStatus: string
  /** currentStatus가 'closed'일 때만 의미 있음 (BL-611) */
  reason?: DCCastroomCloseReason
}

export interface DCPayloadViewersUpdated {
  castroomId: DCCastroomId
  viewers: string[]
  viewerCount: number
}

export interface DCPayloadViewerStatusChanged {
  castroomId: DCCastroomId
  clientId: string
  previousStatus: string
  currentStatus: string
}

export interface DCPayloadMediaStateChanged {
  castroomId: DCCastroomId
  targetRole: 'caster' | 'viewer'
  targetClientId: string
  event: string
  oldState: string
  newState: string
}

export interface DCPayloadMediaQualityReport {
  castroomId: DCCastroomId
  targetRole: 'caster' | 'viewer'
  targetClientId: string
  stats: Record<string, unknown>
}

export interface DCPayloadServerHealth {
  status: string
  mediaServer: {
    type: string
    connected: boolean
  }
  activeCastrooms: number
  activeViewers: number
  uptime: number
}

// ── RTSP Caster Payload ─────────────────────────────────

export interface DCPayloadRtspCasterCreate {
  castroomId: DCCastroomId
  rtspUrl: string
  codec?: 'vp8' | 'h264'
  bitrate?: number
}

export interface DCPayloadRtspCasterStop {
  castroomId: DCCastroomId
}

export type RtspCasterStatus = 'starting' | 'connecting' | 'streaming' | 'error' | 'reconnecting' | 'stopped'

export interface DCPayloadRtspCasterInfo {
  castroomId: DCCastroomId
  rtspUrl: string
  status: RtspCasterStatus
  error?: string
  retryCount?: number
  startedAt?: string
}

export interface DCPayloadRtspCasterResult {
  result: DCResultStatus
  data?: DCPayloadRtspCasterInfo
  error?: string
}

export interface DCPayloadRtspCasterList {
  casters: DCPayloadRtspCasterInfo[]
}

// ── Recording Payload (BL-633) ──────────────────────────
// 설계: dev-docs/DES_CMN_녹화기능_설계.md §8

export interface DCPayloadRecordingStart {
  castroomId: DCCastroomId
  /** 컨테이너 포맷. 1차는 'webm' 만 동작. 미지정 시 서버 기본값 */
  format?: RecordingFormat
  /** 트리거. 1차는 'manual' 만 동작. 미지정 시 'manual' */
  trigger?: RecordingTrigger
}

/**
 * 녹화 관련 오류 코드 — UI에서 사용자 친화적 메시지로 분기 처리하기 위해 사용.
 * 서버 측 DronecastErrorCode와 1:1 매핑.
 */
export type RecordingErrorCode =
  | 'RECORDING_NOT_SUPPORTED'
  | 'RECORDING_LIMIT_EXCEEDED'
  | 'RECORDING_NOT_FOUND'
  | 'RECORDING_ERROR'
  | 'CASTROOM_NOT_FOUND'
  | 'CASTROOM_NOT_READY'

export interface DCPayloadRecordingStartResult {
  result: DCResultStatus
  data?: {
    recordingId: string
    castroomId: DCCastroomId
    startedAt: number
  }
  error?: string
  errorCode?: RecordingErrorCode
}

export interface DCPayloadRecordingStop {
  recordingId: string
}

export interface DCPayloadRecordingStopResult {
  result: DCResultStatus
  data?: RecordingResult
  error?: string
  errorCode?: RecordingErrorCode
}

export interface DCPayloadRecordingList {
  /** 캐스트룸 단위 필터링 (선택). 없으면 활성 녹화 전체 */
  castroomId?: DCCastroomId
}

export interface DCPayloadRecordingListResult {
  result: DCResultStatus
  data?: { recordings: ActiveRecording[] }
  error?: string
  errorCode?: RecordingErrorCode
}

/** 녹화 상태 변경 이벤트 (Admin WS 전용 push) */
export interface DCPayloadRecordingStatusChanged {
  recordingId: string
  castroomId: DCCastroomId
  status: RecordingStatus
  error?: string
}

/** 녹화 종료 이벤트 (Admin WS 전용 push) — finalize·파일 크기 측정 완료 */
export interface DCPayloadRecordingCompleted {
  recording: RecordingResult
}

/** 자동 트리거 발동 이벤트 (Admin WS 전용 push) — BL-637 */
export interface DCPayloadRecordingTriggered {
  recordingId: string
  castroomId: DCCastroomId
  trigger: RecordingTrigger
}

// ── Policy (BL-637) ─────────────────────────────────────

export interface DCPayloadRecordingSetPolicy {
  policy: RecordingPolicy
}

export interface DCPayloadRecordingSetPolicyResult {
  result: DCResultStatus
  data?: { policy: RecordingPolicy }
  error?: string
  errorCode?: RecordingErrorCode
}

export interface DCPayloadRecordingGetPolicy {
  castroomId: DCCastroomId
}

export interface DCPayloadRecordingGetPolicyResult {
  result: DCResultStatus
  /** 정책이 없으면 null */
  data?: { policy: RecordingPolicy | null }
  error?: string
  errorCode?: RecordingErrorCode
}

// ── Wire-format Castroom 정보 ────────────────────────────

export interface DCCastroomInfo {
  status: string
  castroomId: DCCastroomId
  rtspUrl?: string
  numberOfViewers: number
  mediaServerType?: MediaServerType
  /** 영상 소스 입력 유형 */
  sourceType?: VideoSourceType
  /** 목록 UI용 썸네일 공개 URL (캡쳐된 적 있을 때만 제공) */
  thumbnailUrl?: string
  /** 마지막 썸네일 캡쳐 시각 (epoch ms). 캐시 버스팅용 */
  thumbnailUpdatedAt?: number
}

// ── Payload 유니온 ───────────────────────────────────────

export type DCPayload =
  | DCPayloadCastroomCreate
  | DCPayloadCastroomCreateResult
  | DCPayloadCastroomJoin
  | DCPayloadCastroomJoinResult
  | DCPayloadCastroomLeave
  | DCPayloadCastroomLeaveResult
  | DCPayloadCastroomClose
  | DCPayloadCastroomCloseResult
  | DCPayloadCastingStart
  | DCPayloadCastingStartResult
  | DCPayloadStreamSubscribe
  | DCPayloadStreamSubscribeResult
  | DCPayloadStreamUnsubscribe
  | DCPayloadStreamUnsubscribeResult
  | DCPayloadIceCandidate
  | DCPayloadCastroomStatusChanged
  | DCPayloadViewersUpdated
  | DCPayloadViewerStatusChanged
  | DCPayloadMediaStateChanged
  | DCPayloadMediaQualityReport
  | DCPayloadServerHealth
  | DCPayloadRtspCasterCreate
  | DCPayloadRtspCasterStop
  | DCPayloadRtspCasterResult
  | DCPayloadRtspCasterList
  | DCPayloadRtspCasterInfo
  | DCPayloadRecordingStart
  | DCPayloadRecordingStartResult
  | DCPayloadRecordingStop
  | DCPayloadRecordingStopResult
  | DCPayloadRecordingList
  | DCPayloadRecordingListResult
  | DCPayloadRecordingStatusChanged
  | DCPayloadRecordingCompleted
  | DCPayloadRecordingTriggered
  | DCPayloadRecordingSetPolicy
  | DCPayloadRecordingSetPolicyResult
  | DCPayloadRecordingGetPolicy
  | DCPayloadRecordingGetPolicyResult
